import { Box, Button, HStack, SimpleGrid, Text, Spinner, useToast } from '@chakra-ui/react';
import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronLeftIcon, PenIcon } from 'lucide-react';
import { djs } from '@duke-hq/djs';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import {
  useBulkInternalEnrolments,
  InternalEnrolmentMode,
} from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/useBulkInternalEnrolments';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { getCourseDatesFromAdminCourseSchedule } from '@duke-hq/lib/dates';

type SummaryProps = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
};

enum EnrolmentPace {
  FULL_TIME = 'Full time',
  PART_TIME = 'Part time',
}

function SectionHeading({
  children,
  onEditClick,
}: {
  children: React.ReactNode;
  onEditClick: () => void;
}) {
  return (
    <HStack justifyContent="space-between" w="full">
      <Text fontSize="md" fontWeight="semibold" lineHeight="6" color="fg.emphasis">
        {children}
      </Text>
      <Button variant="ghost" onClick={onEditClick} leftIcon={<PenIcon size="20px" />} size="sm">
        <Trans>Edit</Trans>
      </Button>
    </HStack>
  );
}

export function SkillSetSubmission({ goToPrevStep, goToNextStep, setStep }: SummaryProps) {
  const utils = trpcTenant.useUtils();
  const toast = useToast();
  const auth = useAuthTenant();
  const {
    mode,
    selectedMembers,
    selectedCourseAttributes,
    enrolmentSettingsData,
    setCsvErrors,
    setCsvSuccess,
  } = useBulkInternalEnrolments();

  const { data: courseSchedule, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    selectedCourseAttributes.id,
  );

  const bulkInternalEnrolments = trpcTenant.enrolments.bulkInternalEnrolments.useMutation({
    onSuccess: ({ errors, members }) => {
      if (errors?.length) {
        setCsvErrors(errors);
      }

      if (members?.length) {
        setCsvSuccess(members);
      }
    },
    onError: () => {
      toast({
        title: t`Failed to create team member.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled: async () => {
      await Promise.all([
        utils.users.listMembersWithPagination.refetch(),
        utils.enrolments.getMemberInternalEnrolmentCount.refetch(),
        utils.enrolments.all.refetch(),
      ]);

      goToNextStep();
    },
  });

  const courseStartAndEndDates = useMemo(() => {
    if (courseSchedule?.schedule == null || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: courseSchedule.schedule,
        courseSegment: enrolmentSettingsData.course_segment,
        workSchedule: enrolmentSettingsData.work_schedule,
        startAt: enrolmentSettingsData.start_at_date,
      });
    } catch {
      return null;
    }
  }, [
    courseSchedule?.schedule,
    enrolmentSettingsData.course_segment,
    enrolmentSettingsData.start_at_date,
    enrolmentSettingsData.work_schedule,
    isLoading,
  ]);

  const handleSubmit = async () => {
    const buildCourseEnrolmentPayload = () => ({
      members: selectedMembers.map((member) => ({
        ...(mode === InternalEnrolmentMode.NEW ? {} : { id: member.id }),
        email:
          mode === InternalEnrolmentMode.NEW
            ? `${member.email}@${auth?.user.tenant?.email_domain}`
            : member.email,
        firstName: member.firstName,
        lastName: member.lastName,
        isExistingUser: member.isExistingUser,
      })),
      courseId: selectedCourseAttributes.id,
      startAtDate: enrolmentSettingsData.start_at_date,
      contractType: enrolmentSettingsData.enrolment_contract_type,
      contractCategory: enrolmentSettingsData.contract_category,
      workSchedule: enrolmentSettingsData.work_schedule,
      courseSegment: enrolmentSettingsData.course_segment,
      externalId: enrolmentSettingsData?.external_id,
      isInternalEnrolment: true,
    });

    const payload = buildCourseEnrolmentPayload();
    await bulkInternalEnrolments.mutateAsync(payload);
  };

  return (
    <FormMainContainer>
      <FormContentContainer>
        <Box w="full">
          <SectionHeading onEditClick={() => setStep(1)}>
            <Trans>Course</Trans>
          </SectionHeading>
          <SimpleGrid columns={1} w="full">
            <SingleField
              name={selectedCourseAttributes.identifier}
              value={selectedCourseAttributes.title}
            />
            <SingleField
              name={t`Cost`}
              value={`$${selectedCourseAttributes.price_aud.toString()}`}
            />
          </SimpleGrid>
        </Box>
        <Box w="full">
          <SectionHeading onEditClick={() => setStep(3)}>
            <Trans>Enrolment settings</Trans>
          </SectionHeading>
          <SimpleGrid columns={2} w="full">
            <SingleField
              name={<Trans>Manager</Trans>}
              value={`${auth.user.first_name} ${auth.user.last_name} (myself)`}
            />

            <SingleField
              name={<Trans>Start date</Trans>}
              value={djs(enrolmentSettingsData.start_at_date).format('dddd, DD MMMM YYYY')}
            />
            <SingleField
              name={<Trans>Purchase order</Trans>}
              value={enrolmentSettingsData.external_id}
            />
            <SingleField
              name={<Trans>Enrolment pace</Trans>}
              value={EnrolmentPace[enrolmentSettingsData.work_schedule]}
            />
          </SimpleGrid>
          {courseStartAndEndDates?.courseEndDate ? (
            <Text
              color="fg.emphasis"
              textStyle="sm"
              textAlign="left"
              fontWeight="normal"
              w="full"
              mt={2}
            >
              <Trans>Course is expected to end by</Trans>{' '}
              {courseStartAndEndDates?.courseEndDate.long}
            </Text>
          ) : null}
        </Box>
      </FormContentContainer>
      <FormFooterContainer>
        <Button
          onClick={handleSubmit}
          isLoading={bulkInternalEnrolments.isPending}
          loadingText={t`Creating...`}
          variant="primary"
          leftIcon={
            bulkInternalEnrolments.isPending ? <Spinner size="sm" /> : <PenIcon size={16} />
          }
        >
          <Trans>Create and refer participant</Trans>
        </Button>
        <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}
